import { clsx } from "clsx";
import { type ScaleLinear } from "d3";
import { type FC, Fragment } from "react";
import { useHover } from "react-aria";
import { useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";

import styleTrack from "../styles/Track.module.scss";

import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import type { BamSummary, Track, TrackNgsOrderedBamFilesData } from "../../../graphql.js";
import { type SetTooltipContent, ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { NgsCoverageTrackLoader } from "./NgsCoverageTrackLoader.js";
import { NgsReadsTrackLoader } from "./NgsReadsTrackLoader.js";
import type { IAppState } from "@/redux/reducer.ts";

interface INgsTrackProps {
  track: Track;
  interpolate?: boolean;
  handleCustomize: () => void;
  setTooltipContent: SetTooltipContent;
  scale: ScaleLinear<number, number>;
}

const NgsTrack: FC<INgsTrackProps> = ({ track, ...props }) => {
  const { hoverProps, isHovered } = useHover({});
  const emptyTrack = !track.trackCustomization?.ngsConfiguration?.orderedBamFiles?.length;
  const bamDataFiles = useSelector<IAppState, Record<number, BamSummary>>((state) => state.genomeBrowser.bamDatafiles);

  if (emptyTrack) {
    return (
      <div className={"feature-track"}>
        <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
          <TrackHeader
            isTrackHovered={isHovered}
            title={conditionallyPluralizeTrackLabel(track)}
            trackIcons={
              <TriggerButtonGroup vertical>
                <TriggerButton
                  icon={"folder_open"}
                  label={"Select BAM Files"}
                  onPress={() => props.handleCustomize()}
                  size={TriggerButtonSize.Small}
                />
              </TriggerButtonGroup>
            }
          />
          <div className={styleTrack.container}>
            <ShowMessageOverTrack
              message={"Select experiment data (BAM files) to display here"}
              onClick={props.handleCustomize}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {track.trackCustomization.ngsConfiguration.orderedBamFiles.map((bamFile: TrackNgsOrderedBamFilesData, index) => {
        const projectFile = bamDataFiles[bamFile.bamFileId]?.bamFile;
        const unrestrictedAccess = projectFile && !projectFile.hidden && !projectFile.deleted;
        return (
          <Fragment key={`ngs-track-${bamFile.bamFileId}-${index}`}>
            <NgsCoverageTrackLoader
              track={track}
              bamFileId={bamFile.bamFileId}
              scale={props.scale}
              handleCustomize={props.handleCustomize}
              key={`ngs-coverage-track-${bamFile.bamFileId}-${index}`}
              visiblePileup={bamFile.visiblePileup}
            />

            {unrestrictedAccess && bamFile.visiblePileup && (
              <NgsReadsTrackLoader
                track={track}
                bamFileId={bamFile.bamFileId}
                scale={props.scale}
                key={`ngs-pileup-track-${bamFile.bamFileId}-${index}`}
                setTooltipContent={props.setTooltipContent}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export { NgsTrack };
